import { useState, useEffect } from 'react';
import { Clock, CheckCircle, XCircle, AlertCircle, Search, Calendar, Trash2, RefreshCw, Star, MessageSquare, Award, TrendingUp, BookOpen, FileText, Scale, Sparkles, Copy, User } from 'lucide-react';
import { ExecutionHistorySummary } from '../types/executionHistory';
import { ExecutionHistoryService } from '../services/executionHistoryService';
import { EvaluationApiService } from '../services/evaluationApi';
import { AIExecutionScore } from '../types/evaluation';
import { ClearHistoryDialog } from './ClearHistoryDialog';
import { extractConfigFromExecution } from '../utils/extractConfigFromExecution';

interface ExecutionHistoryListProps {
  userId: string;
  onSelectExecution: (executionId: string) => void;
}

export const ExecutionHistoryList = ({ userId, onSelectExecution }: ExecutionHistoryListProps) => {
  const [executions, setExecutions] = useState<ExecutionHistorySummary[]>([]);
  const [aiScores, setAiScores] = useState<Record<string, AIExecutionScore>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [userFilter, setUserFilter] = useState<string>('');
  const [availableUsers, setAvailableUsers] = useState<string[]>([]);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [enqueuingScores, setEnqueuingScores] = useState<Set<string>>(new Set());
  const [copyingConfig, setCopyingConfig] = useState<Set<string>>(new Set());
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const historyService = ExecutionHistoryService.getInstance();
  const evaluationApi = EvaluationApiService.getInstance();

  useEffect(() => {
    loadExecutions();
  }, [userId, searchQuery, statusFilter, userFilter]);

  const loadExecutions = async () => {
    setIsLoading(true);
    try {
      const response = await historyService.searchExecutions({
        query: searchQuery || undefined,
        userId: userFilter || undefined,
        status: (statusFilter as any) || undefined,
        skip: 0,
        take: 50,
      });
      
      // Load scores for visible executions
      const executionIds = response.results.map(e => e.executionId);
      if (executionIds.length > 0) {
        try {
          const scores = await historyService.getScoresBatch(executionIds);
          // Attach score info to executions
          response.results.forEach(execution => {
            execution.scoreInfo = scores[execution.executionId];
          });
        } catch (scoreError) {
          console.error('Failed to load scores:', scoreError);
          // Continue without scores
        }
      }
      
      setExecutions(response.results);
      
      // Build unique user list from results (only when not filtering by user)
      if (!userFilter) {
        const users = Array.from(new Set(
          response.results.map(e => e.userId).filter((u): u is string => !!u)
        )).sort();
        setAvailableUsers(users);
      }
      
      // Load AI scores for evaluation executions (those with _ in ID) using batch request
      const evaluationExecutions = response.results.filter(e => e.executionId.includes('_'));
      if (evaluationExecutions.length > 0) {
        // Extract unique base execution IDs (before the last underscore)
        const baseExecutionIds = Array.from(new Set(
          evaluationExecutions.map(e => {
            const lastUnderscoreIndex = e.executionId.lastIndexOf('_');
            return e.executionId.substring(0, lastUnderscoreIndex);
          })
        ));

        // Batch request - limit to 20 at a time
        const batchSize = 20;
        const aiScoresMap: Record<string, AIExecutionScore> = {};
        
        for (let i = 0; i < baseExecutionIds.length; i += batchSize) {
          const batch = baseExecutionIds.slice(i, i + batchSize);
          try {
            const batchScores = await evaluationApi.getAIScoresBatch({ evaluationExecutionIds: batch });
            
            // Map scores back to full execution IDs (baseId_providerId)
            evaluationExecutions.forEach(execution => {
              const lastUnderscoreIndex = execution.executionId.lastIndexOf('_');
              const baseId = execution.executionId.substring(0, lastUnderscoreIndex);
              const provId = execution.executionId.substring(lastUnderscoreIndex + 1);
              
              const scoresForBase = batchScores[baseId];
              if (scoresForBase) {
                const matchingScore = scoresForBase.find(s => s.providerId === provId);
                if (matchingScore) {
                  aiScoresMap[execution.executionId] = matchingScore;
                }
              }
            });
          } catch (err) {
            console.error(`Failed to load AI scores batch ${i / batchSize + 1}:`, err);
          }
        }
        
        setAiScores(aiScoresMap);
      }
    } catch (error) {
      console.error('Failed to load execution history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'Failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'Blocked':
        return <AlertCircle className="w-5 h-5 text-orange-600" />;
      case 'InProgress':
        return <Clock className="w-5 h-5 text-blue-600 animate-spin" />;
      case 'NoData':
        return <Search className="w-5 h-5 text-gray-500" />;
      case 'NeedsClarification':
        return <MessageSquare className="w-5 h-5 text-amber-600 animate-pulse" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'Failed':
        return 'bg-red-100 text-red-800';
      case 'Blocked':
        return 'bg-orange-100 text-orange-800';
      case 'InProgress':
        return 'bg-blue-100 text-blue-800';
      case 'NoData':
        return 'bg-gray-100 text-gray-600';
      case 'NeedsClarification':
        return 'bg-amber-100 text-amber-800 ring-2 ring-amber-300';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatCost = (cost: number) => {
    return `$${cost.toFixed(4)}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleClearHistory = async (password: string) => {
    setIsClearing(true);
    setClearError(null);
    try {
      // Start the API call (don't await it immediately)
      const clearPromise = historyService.clearAllHistory(password);
      
      // Show loader for 5 seconds minimum
      await new Promise(resolve => setTimeout(resolve, 5000));
      
      // Close dialog and show background processing message
      setShowClearDialog(false);
      setIsClearing(false);
      
      // Show background processing message
      setClearError('Deletion in progress... This may take several minutes. The history will be cleared in the background.');
      
      // Wait for actual API response in background
      try {
        const result = await clearPromise;
        console.log('Clear history result:', result);
        
        // After another 3 seconds, clear the message and reload
        setTimeout(async () => {
          setClearError(null);
          await loadExecutions();
        }, 3000);
      } catch (apiError: any) {
        // If API fails after dialog closed, show error
        const errorMessage = apiError.response?.data?.message || apiError.message || 'Failed to clear history';
        setClearError(`Error: ${errorMessage}`);
      }
    } catch (error: any) {
      console.error('Failed to clear history:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to clear history';
      setClearError(errorMessage);
      setIsClearing(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadExecutions();
    setIsRefreshing(false);
  };

  const handleEnqueueAIScoring = async (executionId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering execution selection
    
    // Extract provider ID (after last underscore)
    const lastUnderscoreIndex = executionId.lastIndexOf('_');
    const providerId = executionId.substring(lastUnderscoreIndex + 1);
    
    console.log('Enqueuing AI scoring:', { executionId, providerId });
    
    setEnqueuingScores(prev => new Set(prev).add(executionId));
    
    try {
      // Use the new endpoint that accepts provider execution ID
      const result = await evaluationApi.enqueueAIScoringByProviderExecutionId(executionId);
      console.log('Enqueue result:', result);
      
      if (result.enqueuedCount === 0) {
        // No requests were enqueued - might already have scores or execution not found
        console.warn('No AI scoring requests were enqueued. The execution might already have scores or was not found.');
        setEnqueuingScores(prev => {
          const next = new Set(prev);
          next.delete(executionId);
          return next;
        });
        alert('No AI scoring requests were enqueued. The execution might already have scores or was not found.');
        return;
      }
      
      // Poll for the AI score until it's available (max 30 seconds)
      const maxAttempts = 6; // 6 attempts * 5 seconds = 30 seconds
      const pollInterval = 5000; // 5 seconds
      let attempts = 0;
      
      const pollForScore = async (): Promise<void> => {
        attempts++;
        console.log(`Polling for AI score (attempt ${attempts}/${maxAttempts})...`);
        
        try {
          const score = await evaluationApi.getAIScore(result.evaluationExecutionId, providerId);
          
          if (score) {
            // Score is available, update the local state
            console.log('AI score received:', score);
            setAiScores(prev => ({
              ...prev,
              [executionId]: score
            }));
            
            setEnqueuingScores(prev => {
              const next = new Set(prev);
              next.delete(executionId);
              return next;
            });
          } else if (attempts < maxAttempts) {
            // Score not available yet, try again
            setTimeout(pollForScore, pollInterval);
          } else {
            // Max attempts reached
            console.warn('AI scoring timed out after 30 seconds');
            setEnqueuingScores(prev => {
              const next = new Set(prev);
              next.delete(executionId);
              return next;
            });
          }
        } catch (_error) {
          console.log(`Poll attempt ${attempts} - score not ready yet (404 expected)`);
          // 404 or other error - continue polling if under max attempts
          if (attempts < maxAttempts) {
            setTimeout(pollForScore, pollInterval);
          } else {
            console.warn('AI scoring timed out after 30 seconds');
            setEnqueuingScores(prev => {
              const next = new Set(prev);
              next.delete(executionId);
              return next;
            });
          }
        }
      };
      
      // Start polling after a short delay
      setTimeout(pollForScore, pollInterval);
      
    } catch (error: any) {
      console.error('Failed to enqueue AI scoring:', error);
      setEnqueuingScores(prev => {
        const next = new Set(prev);
        next.delete(executionId);
        return next;
      });
      // Show error to user
      alert(error.message || 'Failed to enqueue AI scoring');
    }
  };

  const handleCopyConfig = async (executionId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setCopyingConfig(prev => new Set(prev).add(executionId));
    try {
      const detail = await historyService.getExecutionById(executionId);
      if (!detail?.flowData) {
        alert('No execution flow data available for this execution.');
        return;
      }
      const json = await extractConfigFromExecution(detail.flowData, executionId);
      if (!json) {
        alert('Could not extract provider configuration from this execution (no agent steps found).');
        return;
      }
      await navigator.clipboard.writeText(json);
      setCopyFeedback(executionId);
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch (err: any) {
      console.error('Failed to copy config:', err);
      alert(err.message || 'Failed to copy configuration');
    } finally {
      setCopyingConfig(prev => {
        const next = new Set(prev);
        next.delete(executionId);
        return next;
      });
    }
  };

  return (
    <div data-component="ExecutionHistoryList" className="flex flex-col h-full">
      {/* Search and Filters */}
      <div className="p-4 bg-white border-b border-gray-200">
        <div className="flex gap-3 mb-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search executions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Status</option>
            <option value="Completed">Completed</option>
            <option value="Failed">Failed</option>
            <option value="Blocked">Blocked</option>
            <option value="NoData">No Data</option>
            <option value="NeedsClarification">Needs Clarification</option>
            <option value="InProgress">In Progress</option>
          </select>
          <select
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Users</option>
            {availableUsers.map(user => (
              <option key={user} value={user}>{user}</option>
            ))}
          </select>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh history"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowClearDialog(true)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
            title="Clear all history"
          >
            <Trash2 className="w-4 h-4" />
            Clear All
          </button>
        </div>
        {clearError && (
          <div 
            data-flow="message" 
            className={`mt-2 p-3 border rounded-lg text-sm ${
              clearError.startsWith('Deletion in progress') 
                ? 'bg-blue-50 border-blue-200 text-blue-800' 
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            {clearError}
          </div>
        )}
      </div>

      {/* Execution List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div data-flow="loading" className="flex items-center justify-center h-64">
            <div className="text-gray-500">Loading execution history...</div>
          </div>
        ) : executions.length === 0 ? (
          <div data-flow="empty" className="flex flex-col items-center justify-center h-64 text-gray-500">
            <Calendar className="w-16 h-16 mb-4 text-gray-300" />
            <p>No execution history found</p>
            {searchQuery && <p className="text-sm mt-2">Try adjusting your search</p>}
          </div>
        ) : (
          <div data-flow="results" className="divide-y divide-gray-200">
            {executions.map((execution) => (
              <div
                key={execution.executionId}
                onClick={() => onSelectExecution(execution.executionId)}
                className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-1">{getStatusIcon(execution.status)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(
                          execution.status
                        )}`}
                      >
                        {execution.status}
                      </span>
                      <span className="text-xs text-gray-500">{formatDate(execution.startTime)}</span>
                      {execution.isLegacy && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                          Legacy
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-900 font-medium truncate mb-1">
                      {execution.userCommand}
                    </p>
                    {execution.status === 'NeedsClarification' && (
                      <span className="text-xs text-amber-700 font-medium">⏳ Waiting for your answer</span>
                    )}
                    <p className="text-xs text-gray-400 font-mono mb-1">
                      ID: {execution.executionId}
                    </p>
                    {execution.userId && (
                      <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                        <User className="w-3 h-3 flex-shrink-0" />
                        {execution.userId}
                      </p>
                    )}
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      {/* Time - fixed width */}
                      <span className="flex items-center gap-1 w-16">
                        <Clock className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{formatDuration(execution.totalExecutionTimeMs)}</span>
                      </span>
                      {/* Cost - fixed width */}
                      <span className="w-14 truncate">{formatCost(execution.totalCost)}</span>
                      {/* My Score - fixed width */}
                      <span 
                        className={`flex items-center gap-1 w-16 ${execution.scoreInfo?.myScore ? 'text-yellow-600 font-medium' : 'text-gray-400'}`}
                        title={execution.scoreInfo?.myScore ? `My score: ${execution.scoreInfo.myScore}/10` : 'My score: Not provided'}
                      >
                        <Star className={`w-3 h-3 flex-shrink-0 ${execution.scoreInfo?.myScore ? 'fill-yellow-600' : ''}`} />
                        <span className="truncate">{execution.scoreInfo?.myScore ? `${execution.scoreInfo.myScore}/10` : '-'}</span>
                      </span>
                      {/* Average Score - fixed width */}
                      <span 
                        className={`flex items-center gap-1 w-20 ${execution.scoreInfo && execution.scoreInfo.totalScores > 0 ? 'text-gray-600' : 'text-gray-400'}`}
                        title={execution.scoreInfo && execution.scoreInfo.totalScores > 0 
                          ? `Average score: ${execution.scoreInfo.averageScore?.toFixed(1)}/10 from ${execution.scoreInfo.totalScores} user${execution.scoreInfo.totalScores > 1 ? 's' : ''}`
                          : 'Average score: No scores yet'}
                      >
                        <Star className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">
                          {execution.scoreInfo && execution.scoreInfo.totalScores > 0 
                            ? `${execution.scoreInfo.averageScore?.toFixed(1)} (${execution.scoreInfo.totalScores})`
                            : '-'}
                        </span>
                      </span>
                      {/* Comment Icon - fixed width */}
                      <span 
                        className={`flex items-center justify-center w-6 ${execution.scoreInfo?.myNote ? 'text-blue-600' : 'text-gray-300'}`}
                        title={execution.scoreInfo?.myNote || 'No comment'}
                      >
                        <MessageSquare className="w-3 h-3 flex-shrink-0" />
                      </span>
                      {/* AI Scores - all in purple box */}
                      {aiScores[execution.executionId] && (
                        <div className="flex items-center gap-2 px-2 py-0.5 bg-purple-100 rounded">
                          <span className="flex items-center gap-1 text-purple-700 font-medium">
                            <Award className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{aiScores[execution.executionId].overallScore.toFixed(1)}</span>
                          </span>
                          <span className="text-purple-300">|</span>
                          <span 
                            className="flex items-center gap-1 text-purple-600 cursor-help" 
                            title={`Accuracy: ${Math.round(aiScores[execution.executionId].breakdown.accuracy * 100)}%`}
                          >
                            <TrendingUp className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate mr-1">{Math.round(aiScores[execution.executionId].breakdown.accuracy * 100)}%</span>
                          </span>
                          <span 
                            className="flex items-center gap-1 text-purple-600 cursor-help" 
                            title={`Citation Quality: ${Math.round(aiScores[execution.executionId].breakdown.citationQuality * 100)}%`}
                          >
                            <BookOpen className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate mr-1">{Math.round(aiScores[execution.executionId].breakdown.citationQuality * 100)}%</span>
                          </span>
                          <span 
                            className="flex items-center gap-1 text-purple-600 cursor-help" 
                            title={`Completeness: ${Math.round(aiScores[execution.executionId].breakdown.completeness * 100)}%`}
                          >
                            <FileText className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate mr-1">{Math.round(aiScores[execution.executionId].breakdown.completeness * 100)}%</span>
                          </span>
                          <span 
                            className="flex items-center gap-1 text-purple-600 cursor-help" 
                            title={`Legal Quality: ${Math.round(aiScores[execution.executionId].breakdown.legalQuality * 100)}%`}
                          >
                            <Scale className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{Math.round(aiScores[execution.executionId].breakdown.legalQuality * 100)}%</span>
                          </span>
                        </div>
                      )}
                      {/* Copy Config button - any execution */}
                      <button
                        onClick={(e) => handleCopyConfig(execution.executionId, e)}
                        disabled={copyingConfig.has(execution.executionId)}
                        className="relative flex items-center gap-1 px-2 py-0.5 bg-gray-600 text-white rounded text-xs hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Copy provider configuration to clipboard (ProviderSelector import format)"
                        data-flow="copy-config"
                      >
                        <Copy className={`w-3 h-3 flex-shrink-0 ${copyingConfig.has(execution.executionId) ? 'animate-spin' : ''}`} />
                        <span className="truncate">
                          {copyingConfig.has(execution.executionId) ? 'Loading...' : 'Config'}
                        </span>
                        {copyFeedback === execution.executionId && (
                          <span className="absolute -top-7 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                            Copied!
                          </span>
                        )}
                      </button>
                      {/* Enqueue AI Scoring button - only for completed evaluation executions without scores */}
                      {execution.status === 'Completed' && execution.executionId.includes('_') && !aiScores[execution.executionId] && (
                        <button
                          onClick={(e) => handleEnqueueAIScoring(execution.executionId, e)}
                          disabled={enqueuingScores.has(execution.executionId)}
                          className="flex items-center gap-1 px-2 py-0.5 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Enqueue AI scoring for this execution"
                          data-flow="enqueue-ai-scoring"
                        >
                          <Sparkles className={`w-3 h-3 flex-shrink-0 ${enqueuingScores.has(execution.executionId) ? 'animate-spin' : ''}`} />
                          <span className="truncate">
                            {enqueuingScores.has(execution.executionId) ? 'Enqueuing...' : 'AI Score'}
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Clear History Dialog */}
      {showClearDialog && (
        <ClearHistoryDialog
          onConfirm={handleClearHistory}
          onCancel={() => {
            setShowClearDialog(false);
            setClearError(null);
          }}
          isProcessing={isClearing}
        />
      )}
    </div>
  );
};
